import * as React from 'react';
import { observer } from 'mobx-react';
import i18next from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { Organization, ProfileType, Site } from '@ixicare/resources';
import { IconButton, Select, Spinner, Switch } from '@ixicare/ui';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { downloadAsBlob } from '~utils/export';
import { buildOrgTree, buildUnassignedSiteRows, findOrgNode, flattenBillingRows, NO_ORG_VALUE } from './orgTree';
import OrganizationSelect from './OrganizationSelect';
import { RECEIVER_GROUP, useBillingBreakdown } from './useBillingBreakdown';
import { BillingTable } from './BillingTable';
import { buildBillingCsv } from './billingCsv';
import { deviceTypeLabel, isHiddenDeviceType, profileGroup, profileTypeLabel } from './billingLabels';
import { useConcurrentBilling } from './useConcurrentBilling';
import styles from './StatisticsPage.module.css';

const unique = (values: string[]): string[] => Array.from(new Set(values));

// Billing usage is only tracked from June 2026 onward; earlier periods aren't selectable.
const BILLING_START_YEAR = 2026;
const BILLING_START_MONTH = 6;

export const BillingTab: React.FC = observer(() => {
  const { auth } = useStores();
  const now = new Date();
  const [year, setYear] = React.useState<number>(now.getFullYear());
  const [month, setMonth] = React.useState<number>(now.getMonth() + 1);
  const [selectedOrgId, setSelectedOrgId] = React.useState<string | null>(null);

  // Only Watcherr employees manage sites across orgs, so only they get the
  // "no organization" bucket of orphan sites. Org admins never see it.
  const isEmployee = auth.userType === ProfileType.WatcherrEmployee;

  const organizationsQuery = useQuery({
    queryKey: ['organizations'],
    queryFn: Organization.getOrganizations,
  });
  const roots = organizationsQuery.data ?? [];

  const sitesQuery = useQuery({
    queryKey: ['sites'],
    queryFn: () => Site.getSites(),
    enabled: isEmployee,
  });
  const unassignedSites = React.useMemo(
    () => (sitesQuery.data ?? []).filter((site) => site.organizationId === null),
    [sitesQuery.data]
  );

  const orgTree = React.useMemo(() => buildOrgTree(roots), [roots]);

  // Org admins have a single scoped root → preselect it. Watcherr employees see
  // multiple top-level orgs and must pick one before any billing is shown.
  React.useEffect(() => {
    if (selectedOrgId) return;
    if (roots.length === 1) setSelectedOrgId(roots[0].id);
  }, [roots, selectedOrgId]);

  const isNoOrg = selectedOrgId === NO_ORG_VALUE;
  const selectedNode = React.useMemo(
    () => (selectedOrgId && !isNoOrg ? findOrgNode(roots, selectedOrgId) : undefined),
    [roots, selectedOrgId, isNoOrg]
  );
  const hasSelection = isNoOrg || !!selectedNode;

  const billingRows = React.useMemo(() => {
    if (isNoOrg) return buildUnassignedSiteRows(unassignedSites);
    return selectedNode ? flattenBillingRows(selectedNode) : undefined;
  }, [isNoOrg, unassignedSites, selectedNode]);

  const breakdown = useBillingBreakdown(billingRows, selectedOrgId, year, month);
  const rows = breakdown.data ?? [];

  // The flag is per organization and not inherited, so this is the setting of the
  // organization currently selected — sub-organizations are enabled by selecting them.
  const concurrentBilling = useConcurrentBilling(isNoOrg ? null : selectedOrgId);
  // Show the seat columns as soon as any row is on this billing model, so a partner
  // sees a newly enabled customer without having to drill into it.
  const showSeats = rows.some((row) => row.usage.concurrentBillingEnabled);
  const showThirdParty = rows.some((row) => row.usage.thirdPartyDevices.totalCount > 0);

  const deviceTypes = React.useMemo(
    () =>
      unique(rows.flatMap((row) => row.usage.devices.items.map((item) => item.deviceType)))
        .filter((type) => !isHiddenDeviceType(type))
        .sort((a, b) => deviceTypeLabel(a).localeCompare(deviceTypeLabel(b))),
    [rows]
  );
  const profileTypes = React.useMemo(() => {
    const types = unique(rows.flatMap((row) => row.usage.profiles.items.map((item) => profileGroup(item.profileType))));
    // On concurrent billing the receiver column carries the peak, so it has to be
    // there even when nobody is billable by headcount.
    if (showSeats && !types.includes(RECEIVER_GROUP)) types.push(RECEIVER_GROUP);
    return types.sort((a, b) => profileTypeLabel(a).localeCompare(profileTypeLabel(b)));
  }, [rows, showSeats]);

  // In the start year the months below June are hidden; later years show all 12.
  const monthOptions = React.useMemo(() => {
    const firstMonth = year === BILLING_START_YEAR ? BILLING_START_MONTH : 1;
    return Array.from({ length: 12 - firstMonth + 1 }, (_, index) => {
      const monthNumber = firstMonth + index;
      return {
        value: monthNumber,
        label: new Date(2000, monthNumber - 1, 1).toLocaleString(i18next.language, { month: 'long' }),
      };
    });
  }, [year]);
  const yearOptions = React.useMemo(() => {
    const years: number[] = [];
    for (let y = now.getFullYear(); y >= BILLING_START_YEAR; y--) years.push(y);
    return years.map((y) => ({ value: y, label: String(y) }));
  }, []);

  // Switching to the start year can leave the month before the floor — clamp it up.
  React.useEffect(() => {
    if (year === BILLING_START_YEAR && month < BILLING_START_MONTH) setMonth(BILLING_START_MONTH);
  }, [year, month]);

  const handleExport = () => {
    const csv = buildBillingCsv(rows, deviceTypes, profileTypes, showSeats, showThirdParty);
    downloadAsBlob(`active-${year}-${String(month).padStart(2, '0')}.csv`, csv);
  };

  const renderBody = () => {
    if (!hasSelection) {
      return <p>{t('statistics.selectOrganizationPrompt')}</p>;
    }
    if (breakdown.isLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spinner />
        </div>
      );
    }
    if (breakdown.isError) {
      return <p>{t('statistics.loadError')}</p>;
    }
    if (rows.length === 0) {
      return <p>{t('statistics.empty')}</p>;
    }
    return (
      <BillingTable
        rows={rows}
        deviceTypes={deviceTypes}
        profileTypes={profileTypes}
        showSeats={showSeats}
        showThirdParty={showThirdParty}
      />
    );
  };

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 280 }}>
          <label style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>{t('billing.organization') + ':'}</label>
          <div style={{ flex: 1 }}>
            <OrganizationSelect
              tree={orgTree}
              value={selectedOrgId}
              onChange={setSelectedOrgId}
              extraOption={isEmployee ? { value: NO_ORG_VALUE, label: t('billing.noOrganization') } : undefined}
            />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>{t('billing.month') + ':'}</label>
          <Select value={month} onChange={(value: number) => setMonth(value)} options={monthOptions} as="filter" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>{t('billing.year') + ':'}</label>
          <Select value={year} onChange={(value: number) => setYear(value)} options={yearOptions} as="filter" />
        </div>
        {!isNoOrg && selectedOrgId && concurrentBilling.mayEdit && (
          <div className={styles.switchFilter}>
            <Switch
              label={t('statistics.seats.enableSetting')}
              checked={concurrentBilling.enabled}
              disabled={concurrentBilling.isLoading || concurrentBilling.isSaving}
              onChange={(checked: boolean) => concurrentBilling.setEnabled(checked)}
            />
          </div>
        )}
        {rows.length > 0 && (
          <div style={{ marginLeft: 'auto' }}>
            <IconButton lucide={Download} onClick={handleExport} />
          </div>
        )}
      </div>
      {renderBody()}
    </>
  );
});
